/* El semáforo de una persona en la nómina.
 *
 * Pedido de Nico (26/08/2026, ticket #148): *«Quiero que el nombre de los
 * empleados activos tenga al lado un círculo verde. Los que tienen novedades
 * en amarillo (que aclare por qué) y los inactivos en rojo»*.
 *
 * ── «QUE ACLARE POR QUÉ» ES LA MITAD DEL PEDIDO ─────────────────────────────
 *
 * Un círculo amarillo sin decir de qué manda a abrir la ficha para averiguarlo,
 * y el que abre cinco para encontrar una deja de abrirlas. Por eso esto
 * devuelve el motivo escrito —«Enfermedad desde el 12/08»— y no sólo el color.
 * Es el mismo criterio que el semáforo del CRT en Aduana.
 *
 * ── QUÉ CUENTA COMO «TENER NOVEDADES» ───────────────────────────────────────
 *
 * Las VIGENTES HOY, y no todas las que tuvo alguna vez. Una suspensión que
 * terminó en marzo no es una novedad de hoy: si contara, el amarillo se le
 * pegaría a la persona para siempre y en un año sería el color de media
 * nómina. Lo que ya pasó está en su ficha, que es donde se lo consulta.
 *
 * Una NOVEDAD sin `hasta` sí está vigente: es una que todavía no terminó —un
 * parte de enfermo abierto—, no una que terminó y nadie anotó. Se la trata
 * como abierta porque es lo que dice el dato, y cerrar por nuestra cuenta lo
 * que nadie cerró sería inventar una fecha.
 *
 * Con las LICENCIAS no vale lo mismo, y el dato es el que lo dice. De 2.704
 * sin `hasta`, 2.313 son anteriores a 2020 y la más vieja es de 2007: el
 * vacío está parejo en todos los años —95 de 96 en 2016, 69 de 69 en 2023— y
 * sólo 7 caen en los últimos dos meses. No es «un parte abierto», es una
 * columna que el import del histórico de ausentismo nunca trajo. Tratarlas
 * como abiertas pintaba a 110 personas del padrón como ausentes HOY por una
 * licencia de hace quince años, y ese amarillo permanente es justo lo que
 * este módulo existe para evitar. Sin fecha de fin no se afirma que corra:
 * se muestra en la ficha como «sin fecha de fin», que es lo único cierto.
 *
 * ── LAS DOS FUENTES, COMO EN LA FICHA ───────────────────────────────────────
 *
 * `rrhh_novedades` y `licencias` son tablas distintas y las dos son novedades
 * para el que mira — lo fijó `lineaDeTiempo` en `rrhhNovedades.js` y acá se
 * respeta. Una licencia no se copia a novedades: se lee de su tabla.
 *
 * Acá no hay red: esto es cuenta y texto. Lo que habla con la base está en
 * `services/`.
 */

import { rotuloTipo } from "./rrhhNovedades";

export const SEMAFORO_NOMINA = {
  verde: { icono: "🟢", label: "Activo", que: "Trabaja y no tiene novedades" },
  amarillo: {
    icono: "🟡",
    label: "Con novedad",
    que: "Trabaja, pero tiene una novedad vigente",
  },
  rojo: { icono: "🔴", label: "Inactivo", que: "Ya no trabaja acá" },
};

export const comoSemaforoNomina = (c) =>
  SEMAFORO_NOMINA[String(c || "").trim()] || null;

/* ── Fechas ────────────────────────────────────────────────────────────────
 *
 * Se comparan como TEXTO `YYYY-MM-DD` y no como `Date`, a propósito: `new
 * Date("2026-08-28")` es medianoche UTC, que en Argentina es el 27 a las 21 —
 * y una novedad que empieza hoy se leería como que empieza mañana. En formato
 * ISO el orden alfabético y el cronológico son el mismo.
 */
const soloFecha = (v) => String(v ?? "").trim().slice(0, 10);

/* Hoy en Argentina, con el desplazamiento fijo de -3 que usa todo el hub. Un
 * cambio de huso no puede mover el día en el que una licencia está vigente. */
export function hoyAr(ahora = new Date()) {
  const a = new Date(ahora.getTime() - 3 * 3600 * 1000);
  const p2 = (n) => String(n).padStart(2, "0");
  return `${a.getUTCFullYear()}-${p2(a.getUTCMonth() + 1)}-${p2(a.getUTCDate())}`;
}

export function estaVigente(item, hoy = hoyAr()) {
  if (!item || item.anulado_en) return false;
  const desde = soloFecha(item.desde);
  // SIN `desde` NO SE PUEDE AFIRMAR QUE ESTÉ VIGENTE. Es una fila incompleta,
  // y darla por vigente pintaría de amarillo a alguien por un dato que falta.
  if (!desde || desde > hoy) return false;
  const hasta = soloFecha(item.hasta);
  // Una LICENCIA sin fin tampoco se afirma: ver el bloque de arriba. El
  // `origen` lo pone `vigentesPorDni` al juntar las dos tablas, así que una
  // fila suelta —sin origen— sigue con la regla vieja y nada se rompe.
  if (!hasta) return item.origen !== "licencia";
  return hasta >= hoy;
}

const comoDDMM = (iso) => {
  const t = soloFecha(iso);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(t)) return "";
  const [a, m, d] = t.split("-");
  return `${d}/${m}/${a}`;
};

/* El motivo, en una frase. `licencias` guarda el tipo como texto libre y
 * `rrhh_novedades` con una clave del catálogo: se traduce sólo la segunda. */
export function comoSeLeeLaNovedad(item) {
  const esLicencia = item?.origen === "licencia";
  const que = esLicencia
    ? String(item?.tipo ?? "").trim() || "Licencia"
    : rotuloTipo(item?.tipo);
  const desde = comoDDMM(item?.desde);
  const hasta = comoDDMM(item?.hasta);
  if (desde && hasta) return `${que} del ${desde} al ${hasta}`;
  if (desde) return `${que} desde el ${desde}`;
  return que;
}

/* ── EL SEMÁFORO ───────────────────────────────────────────────────────────
 *
 * ROJO GANA. Una persona dada de baja que además tenía una licencia abierta el
 * día que se fue no es «con novedad»: ya no trabaja, y ése es el dato. Mismo
 * orden que en el CRT — el estado que cierra el tema va primero.
 */
export function semaforoPersona(persona, novedades = []) {
  if (!persona) return null;
  if (!persona.trabaja) return "rojo";
  return (novedades || []).length > 0 ? "amarillo" : "verde";
}

/* Las novedades vigentes de cada DNI, listas para la fila.
 *
 * ── SE ARMA UN ÍNDICE Y NO SE FILTRA POR PERSONA ───────────────────────────
 *
 * Son 292 filas en pantalla; buscar dentro de la lista de novedades en cada
 * una es 292 recorridas de la misma lista. Con el índice se recorre una vez.
 *
 * El DNI se normaliza a texto sin puntos: la nómina lo guarda como `text` y
 * las novedades también, pero una de las dos puertas de carga podría dejar un
 * `12.345.678`, y entonces la novedad existiría y no se le pegaría a nadie —
 * fallando en silencio, que es lo peor que puede hacer.
 */
export const claveDni = (v) =>
  String(v ?? "").replace(/\D+/g, "").replace(/^0+/, "");

export function vigentesPorDni(novedades, licencias, hoy = hoyAr()) {
  const idx = {};
  const sumar = (filas, origen) => {
    for (const f of filas || []) {
      const item = { ...f, origen };
      if (!estaVigente(item, hoy)) continue;
      const k = claveDni(f.dni);
      if (!k) continue;
      (idx[k] = idx[k] || []).push(item);
    }
  };
  sumar(novedades, "novedad");
  sumar(licencias, "licencia");
  return idx;
}
